import { useState, useEffect, useCallback, useRef } from "react";
import { ApiGet } from "../helpers/API/ApiData";
import { toast } from "react-toastify";

interface UsePaginatedApiOptions {
  /** Items per page (default: 10) */
  countPerPage?: number;
  /** Query parameter name for search (default: "letter") */
  searchParam?: string;
  /** Additional static query params appended to every request */
  extraParams?: string;
  /**
   * Custom extractor for the data array from the API response.
   * Default: `res?.data?.data` (flat) or pass a function for nested shapes.
   */
  extractData?: (res: any) => any[];
  /**
   * Custom extractor for the total count from the API response.
   * Default: `res?.data?.count`
   */
  extractCount?: (res: any) => number;
}

interface UsePaginatedApiReturn<T> {
  data: T[];
  loading: boolean;
  search: string;
  page: number;
  count: number;
  countPerPage: number;
  handleSearch: (value: string) => void;
  handlePageChange: (newPage: number) => void;
  handleCountPerPageChange: (newCount: number) => void;
  refetch: () => void;
}

/**
 * Reusable hook for paginated API fetching with search.
 *
 * Handles the common pattern shared across ~13 CRUD pages:
 *   search state, page state, count state, loading state, and fetch logic.
 *
 * @param endpoint - API endpoint path (e.g., "company-profile/find")
 * @param options  - Configuration for pagination, search param name, extra params
 *
 * @example
 * ```ts
 * const { data, loading, page, count, countPerPage, handleSearch, handlePageChange, refetch }
 *   = usePaginatedApi<Company>("company-profile/find", { extraParams: "isActive=true" });
 * ```
 */
export function usePaginatedApi<T = any>(
  endpoint: string,
  options?: UsePaginatedApiOptions
): UsePaginatedApiReturn<T> {
  const countPerPage = options?.countPerPage ?? 10;
  const searchParam = options?.searchParam ?? "letter";
  const extraParams = options?.extraParams ?? "";
  const defaultExtractData = useCallback((res: any) => res?.data?.data ?? [], []);
  const defaultExtractCount = useCallback((res: any) => res?.data?.count ?? 0, []);
  const extractData = options?.extractData ?? defaultExtractData;
  const extractCount = options?.extractCount ?? defaultExtractCount;

  // Stable refs for extractors to avoid infinite re-fetch loops
  const extractDataRef = useRef(extractData);
  const extractCountRef = useRef(extractCount);
  extractDataRef.current = extractData;
  extractCountRef.current = extractCount;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [perPage, setPerPage] = useState(countPerPage);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const parts = [`page=${page}`, `limit=${perPage}`];
      if (search) parts.push(`${searchParam}=${search}`);
      if (extraParams) parts.push(extraParams);
      const query = parts.join("&");

      const res = await ApiGet(`${endpoint}?${query}`);
      setData(extractDataRef.current(res));
      setCount(extractCountRef.current(res));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
    } finally {
      setLoading(false);
    }
  }, [endpoint, page, perPage, search, searchParam, extraParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleCountPerPageChange = useCallback((newCount: number) => {
    setPerPage(newCount);
    setPage(1);
  }, []);

  return {
    data,
    loading,
    search,
    page,
    count,
    countPerPage: perPage,
    handleSearch,
    handlePageChange,
    handleCountPerPageChange,
    refetch: fetchData,
  };
}
